<template>
  <div class="namespace-page">
    <div class="namespace-page__side-menu">
      <RouterVerticalTabs>
        <template #default="{ currentTab }">
          <RouterVerticalTab
            :route="{ name: 'namespace-queues' }"
            label="Обзор рабочей области"
            :current-tab="currentTab"
          >
            <template #left>
              <QueueListIcon />
            </template>
          </RouterVerticalTab>
          <template v-if="namespaceAllowed">
            <RouterVerticalTab
              :route="{ name: 'namespace-settings' }"
              label="Основная информация"
              :current-tab="currentTab"
            >
              <template #left>
                <Cog6ToothIcon />
              </template>
            </RouterVerticalTab>
            <RouterVerticalTab
              :route="{ name: 'namespace-admins' }"
              label="Администраторы рабочей области"
              :current-tab="currentTab"
            >
              <template #left> <UserIcon /> </template>
            </RouterVerticalTab>
            <RouterVerticalTab
              :route="{ name: 'namespace-team' }"
              label="Команда"
              :current-tab="currentTab"
            >
              <template #left> <UserGroupIcon /> </template>
            </RouterVerticalTab>
            <RouterVerticalTab
              :route="{ name: 'namespace-types' }"
              label="Типы"
              :current-tab="currentTab"
            >
              <template #left> <RectangleStackIcon /> </template>
            </RouterVerticalTab>
            <RouterVerticalTab
              :route="{ name: 'namespace-difficulty' }"
              label="Сложность"
              :current-tab="currentTab"
            >
              <template #left> <ChartBarIcon /> </template>
            </RouterVerticalTab>
          </template>
        </template>
      </RouterVerticalTabs>
    </div>
    <div class="namespace-page__content" :class="contentClasses">
      <RouterView :userData="userData" :namespace="namespace" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onBeforeMount, provide, ref, Ref, watch } from 'vue';
import { RouterVerticalTab, RouterVerticalTabs } from '@/components';
import {
  Cog6ToothIcon,
  QueueListIcon,
  UserGroupIcon,
  UserIcon,
  RectangleStackIcon,
  ChartBarIcon,
} from 'tir-style-system/icons/outline';
import { ProjectModel } from '@/models';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { AdapterService } from '../namespace-admins-page/adapter/AdapterService';
import { useRouter } from 'vue-router';
import ShortProfile from '@/api/Main/dataSource/api/users/profile/i-service/models/ShortProfile';
import { NamespaceAdapterService } from './adapter-service';
import { NamespaceModel } from '../project-namespaces-page/models';

/**Апи сервис */
const apiService = new NamespaceAdapterService();

const props = defineProps({
  userData: { type: Array<ShortProfile> },
});

/**Маршруты */
const router = useRouter();
/**Сервис для получения админов */
const adapter = new AdapterService();
/**Информация об области */
const namespace = ref<NamespaceModel>();

/**Информация о проекте */
const project = inject('project') as Ref<ProjectModel>;

/**Есть доступ к роуту */
const namespaceAllowed = computed(() => {
  const isOwner = CurrentUser.Roles.includes(UserRoleEnum.Owner);
  const isProjectAdmin = !!project.value.Admins?.some((a) => a.Id == CurrentUser.Id);
  const isNamespaceAdmin = namespaceAdmin.value;
  return isOwner || isProjectAdmin || isNamespaceAdmin;
});
/**Id рабочей области */
const namespaceId = computed(() => router.currentRoute.value.params?.namespaceId?.toString());

/**Классы элемента с контентом */
const contentClasses = computed(() => ({
  form:
    router.currentRoute.value.name == 'namespace-types' ||
    router.currentRoute.value.name == 'namespace-difficulty',
}));

/**Id админов рабочей области */
const namespaceAdminsIds = ref<number[]>([]);

provide('popover-scroll-selector', '.project-list-wrapper-content');

/**Перед рендером */
onBeforeMount(async () => {
  await getNamespace();
  await getNamespaceAdmins();

  const checkoutRoutes = [
    'namespace-settings',
    'namespace-types',
    'namespace-difficulty',
    'namespace-team',
    'namespace-admins',
  ];
  if (!namespaceAllowed.value && checkoutRoutes.includes(String(router.currentRoute.value.name))) {
    router.push('/');
  }
});

const namespaceAdmin = ref<boolean>(false);

provide('isAdminNamespace', namespaceAdmin);
/**Информация НС */
provide('namespaceInfo', namespace);

watch(
  () => router.currentRoute.value.params.namespaceId,
  async () => {
    const adminsNamespace = ref();
    const resp = await adapter.getNamespaceAdmins(namespaceId.value);
    if (resp.IsSuccess) {
      adminsNamespace.value = resp.Value.map((user) => user.Id);
    }
    namespaceAdmin.value = adminsNamespace.value.includes(CurrentUser.Id);
  },
  { immediate: true },
);

/**Получить список админов неймспейса */
const getNamespaceAdmins = async () => {
  if (!namespaceId.value) return;

  const resp = await adapter.getNamespaceAdmins(namespaceId.value);
  if (resp.IsSuccess) {
    namespaceAdminsIds.value = resp.Value.map((user) => user.Id);
  }
};
/**Получение областей */
const getNamespace = async () => {
  const response = await apiService.getNamespace(namespaceId.value);
  if (response.IsSuccess && response.Value) {
    namespace.value = response.Value;
  } else {
    router.push({
      name: 'project-stub-page',
      query: { errorCode: response.ErrorCode },
    });
  }
};
</script>

<style lang="scss">
.namespace-page {
  display: grid;
  grid-template-columns: 20.188rem 1fr;
  gap: 0.75rem;
  min-height: calc(100vh - 10.5rem);

  &__side-menu,
  &__content {
    border-radius: 0.5rem;
    background: var(--neutral-0, #fff);
    box-shadow: 0rem 0.25rem 0.5rem 0rem rgba(0, 0, 0, 0.04),
      0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.04);
  }

  &__side-menu {
    overflow: hidden;
  }

  &__content {
    padding: 1.5rem 1.25rem;
    &.form {
      padding: 0.75rem;
      .template-import-form_sidebar {
        right: calc(100% + 1.5rem);
        border: none;
        width: 20.1875rem;
      }
    }
  }
}
</style>
